/**
 * PersistSiteMapViewport — writes pan/zoom/map-type to localStorage on idle.
 *
 * Child of `<Map>` from `@vis.gl/react-google-maps`. Debounced so rapid
 * gestures coalesce into one write.
 */
import { useEffect, useRef } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import { writeLastSiteMapViewport } from '../../lib/siteMapViewportMemory';

const DEBOUNCE_MS = 500;

export interface PersistSiteMapViewportProps {
  siteId: string;
}

export function PersistSiteMapViewport({ siteId }: PersistSiteMapViewportProps) {
  const map = useMap();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!map || !siteId) return;

    const persist = () => {
      const center = map.getCenter();
      const zoom = map.getZoom();
      if (!center || zoom == null || !Number.isFinite(zoom)) return;
      writeLastSiteMapViewport(siteId, {
        center: { lat: center.lat(), lng: center.lng() },
        zoom,
        mapTypeId: map.getMapTypeId() ?? undefined,
      });
    };

    const schedulePersist = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(persist, DEBOUNCE_MS);
    };

    const idleListener = map.addListener('idle', schedulePersist);
    return () => {
      idleListener.remove();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [map, siteId]);

  return null;
}
